/**
 * Telemetry for the chat stream's connection — what the SSE stream did when it
 * stopped delivering, and whether the user got their answer anyway.
 *
 * ## Scope, and what this deliberately does NOT cover
 *
 * This covers genuine socket death on a live turn. It does NOT cover the
 * asynchronous workflow path: `workflow_run` returns in ~25ms and the stream
 * closes cleanly minutes before the run finishes, so a lost workflow result
 * never produces a stream failure at all. That path is instrumented separately
 * — see `workflowDelivery.ts`. Conflating the two was the original mistake here.
 *
 * ## Two events, because the diagnosis must survive the user leaving
 *
 * `om_stream_stalled` fires the INSTANT the stall is detected. Recovery from
 * the persisted transcript can run for up to 18 minutes
 * (`STALL_RECOVERY_WINDOW_MS`), and the whole catch arm is guarded by
 * `mountedRef.current` — so a user who gives up and navigates away recorded
 * absolutely nothing under a single after-recovery event. That user is exactly
 * the one whose turn was lost.
 *
 * `om_stream_outcome` follows with the verdict once recovery settles. The two
 * join on `turnId`. A stall with no matching outcome is itself a finding: the
 * user left before the answer arrived.
 *
 * PII rule: ids, counts, enums and booleans. Never message text, never an
 * identity. Note the reason codes are a closed set and `detail` carries no
 * server message — a transport `TypeError`'s text can embed a URL.
 */

import { recordEvent } from '../../telemetry/rum.ts';
import type { StreamStats } from '../../agents/agentStream.ts';

/** Identity of the turn a stream event belongs to. */
export interface StreamContext {
  /** Correlates with the runtime's `turn.stream` log line. */
  readonly turnId: string;
  readonly agentId: string;
}

/** Flatten stats for CloudWatch Insights — nested keys are painful to query,
 *  and absent fields stay absent rather than becoming misleading sentinels. */
function payload(
  stats: StreamStats,
  ctx: StreamContext,
): Record<string, string | number | boolean> {
  return {
    reason: stats.reason,
    turnId: ctx.turnId,
    agentId: ctx.agentId,
    elapsedMs: stats.elapsedMs,
    frameCount: stats.frameCount,
    textFrames: stats.frames['text-delta'] ?? 0,
    toolFrames: (stats.frames['tool-call-start'] ?? 0) + (stats.frames['tool-call-end'] ?? 0),
    keepalives: stats.keepalives,
    malformedFrames: stats.malformedFrames,
    hadWorkflow: stats.hadWorkflow,
    ...(stats.lastFrameType !== undefined ? { lastFrameType: stats.lastFrameType } : {}),
    ...(stats.lastFrameAgoMs !== undefined ? { lastFrameAgoMs: stats.lastFrameAgoMs } : {}),
    ...(stats.workflowRunId !== undefined ? { workflowRunId: stats.workflowRunId } : {}),
  };
}

/**
 * The stream stopped delivering. Emitted immediately, before recovery is
 * attempted, so the diagnosis lands even if the user closes the tab.
 */
export function recordStreamStalled(stats: StreamStats, ctx: StreamContext): void {
  try {
    recordEvent('om_stream_stalled', payload(stats, ctx));
  } catch {
    // `recordEvent` guards the SDK call, but FLATTENING runs before it, and
    // this sits inside the stall-recovery path — a throw here would abort the
    // recovery it exists to observe, turning a recoverable turn into a lost
    // one. Telemetry must never perturb the flow it observes.
  }
}

/** How a stalled turn ended for the user. */
export interface StreamOutcome {
  /** The reply was recovered from the transcript and shown. */
  readonly recovered: boolean;
  /** ms spent in transcript recovery. */
  readonly recoveryMs: number;
  /** Recovery polls issued. */
  readonly recoveryPolls: number;
  /**
   * Recovery polls that FAILED to fetch.
   *
   * Without this, 18 minutes of the sessions endpoint 500ing is byte-identical
   * to 18 minutes of the runtime never writing the transcript — two completely
   * different on-call responses.
   */
  readonly recoveryFetchFailures: number;
}

/** The verdict for a stalled turn: did the user end up with their answer? */
export function recordStreamOutcome(
  stats: StreamStats,
  ctx: StreamContext,
  outcome: StreamOutcome,
): void {
  try {
    recordEvent('om_stream_outcome', {
      ...payload(stats, ctx),
      recovered: outcome.recovered,
      recoveryMs: outcome.recoveryMs,
      recoveryPolls: outcome.recoveryPolls,
      recoveryFetchFailures: outcome.recoveryFetchFailures,
    });
  } catch {
    // See recordStreamStalled: flattening runs outside recordEvent's guard.
  }
}
